import { API_ENDPOINTS } from "@/config/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const API_TIMEOUT = 30000; // 30 seconds

type UnauthorizedListener = () => void;

const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to 401s from API wrappers. Login 401s are not reported. Returns unsubscribe. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

/** Report a 401 from a direct fetch that bypasses fetchApi. Login 401s are ignored. */
export function reportUnauthorized(endpoint: string, status: number): void {
  if (status !== 401 || endpoint === API_ENDPOINTS.login) return;
  for (const listener of unauthorizedListeners) {
    try {
      listener();
    } catch {
      // A listener must not replace the ApiError the caller still receives.
    }
  }
}

function buildInit(options: RequestInit, signal: AbortSignal): RequestInit {
  return {
    ...options,
    signal,
    credentials: "include", // Include cookies for auth
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };
}

async function sendRequest(
  endpoint: string,
  options: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  try {
    return await fetch(endpoint, buildInit(options, controller.signal));
  } finally {
    clearTimeout(timeoutId);
  }
}

// The access cookie is short-lived and tools never refreshed it, so every
// expiry would otherwise be a forced logout. Concurrent 401s share one call.
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = sendRequest(API_ENDPOINTS.refresh, { method: "POST" })
      .then((response) => response.ok)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function canRefreshOn401(endpoint: string): boolean {
  return (
    endpoint !== API_ENDPOINTS.login &&
    endpoint !== API_ENDPOINTS.logout &&
    endpoint !== API_ENDPOINTS.refresh
  );
}

async function toApiError(response: Response): Promise<ApiError> {
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  let errorData: unknown;
  try {
    errorData = await response.json();
    if (
      typeof errorData === "object" &&
      errorData !== null &&
      "detail" in errorData
    ) {
      errorMessage = String(errorData.detail);
    }
  } catch {
    // Ignore JSON parse errors
  }
  return new ApiError(errorMessage, response.status, errorData);
}

/**
 * Send a request and return the 2xx response. On a 401 it refreshes the
 * session once and retries once (options are reused, so string/FormData
 * bodies are fine); only if that fails does it report the 401 and throw.
 */
async function requestOk(
  endpoint: string,
  options: RequestInit,
): Promise<Response> {
  let response = await sendRequest(endpoint, options);
  if (response.status === 401 && canRefreshOn401(endpoint)) {
    if (await refreshSession()) {
      response = await sendRequest(endpoint, options);
    }
  }
  if (!response.ok) {
    const apiError = await toApiError(response);
    reportUnauthorized(endpoint, response.status);
    throw apiError;
  }
  return response;
}

async function withApiErrors<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError("Request timeout", 408);
    }

    throw new ApiError(
      error instanceof Error ? error.message : "An unknown error occurred",
      500,
    );
  }
}

export function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  return withApiErrors(async () => {
    const response = await requestOk(endpoint, options);
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  });
}

/** Like fetchApi but returns status + parsed body for 2xx (used for 202 + job_id flows). */
export function fetchApiWithStatus(
  endpoint: string,
  options: RequestInit = {},
): Promise<{ status: number; data: unknown }> {
  return withApiErrors(async () => {
    const response = await requestOk(endpoint, options);
    const data = await response.json();
    return { status: response.status, data };
  });
}
